import { get, ref, set, remove, push, update } from "firebase/database";
import { db } from "./api_firebase";
import { obtenerNits, CONFIG, log } from "./variables_globales";
import { crearLog } from "../pages/components/logs/logs";

// Lee cada ruta ({nit} se reemplaza por cada conjunto) y devuelve [{ ruta, valor }].
const leerRutas = async (rutas, esSuper) => {
  const nits = esSuper ? await obtenerNits() : [CONFIG.codigoCliente()];
  const rutasCompletas = nits.flatMap(nit => rutas.map(ruta => ruta.replace("{nit}", nit)));
  return Promise.all(rutasCompletas.map(async ruta => {
    crearLog(ruta, "leer");
    const snapshot = await get(ref(db, ruta));
    return { ruta, valor: snapshot.val() || {} };
  }));
};

// Como consultarMultiple, pero devuelve una lista donde cada registro trae su id (clave de Firebase)
// y rutaRegistro, la ruta exacta donde está guardado (para eliminarlo sin recalcularla).
export const consultarConRuta = async (rutas, esSuper = CONFIG.esSuper()) => {
  try {
    const resultados = await leerRutas(rutas, esSuper);
    return resultados.flatMap(({ ruta, valor }) =>
      Object.entries(valor).map(([id, registro]) => ({ ...registro, id, rutaRegistro: `${ruta.replace(/\/$/, "")}/${id}` }))
    );
  } catch (error) {
    console.error("❌ Error en consulta con ruta:", error.message);
    return [];
  }
};

export const consultarMultiple = async (rutas, esSuper = CONFIG.esSuper()) => {
  if (!rutas) throw new Error("Se requiere una ruta válida");

  log("📡 Consultando rutas:", rutas);

  try {
    const resultados = await leerRutas(rutas, esSuper);
    const datosUnidos = resultados.reduce(
      (acumulador, { valor }) => Object.assign(acumulador, valor),
      {}
    );

    log("✅ Datos obtenidos:", datosUnidos);
    return datosUnidos;
  } catch (error) {
    console.error("❌ Error en consulta múltiple:", error.message);
    return {};
  }
};

export const consultarSimple = async (ruta) => {
  if (!ruta) throw new Error("Se requiere una ruta válida");

  log("📡 Consultando:", ruta);

  try {
    crearLog(ruta, "leer");

    const snapshot = await get(ref(db, ruta));
    const datos = snapshot.val() || {};
    log("✅ Datos obtenidos:", datos);

    return datos;
  } catch (error) {
    console.error(`❌ Error al consultar ${ruta}:`, error.message);
    return {};
  }
};

export const crearRegistro = async (ruta, datos) => {
  if (!ruta || !datos) throw new Error("Se requieren ruta y datos válidos");

  try {
    console.log("➕ Creando registro en:", ruta);
    const nuevoRegistro = push(ref(db, ruta));
    await set(nuevoRegistro, datos);
    console.log("✅ Registro creado con ID:", nuevoRegistro.key);

    crearLog(ruta, "crear");

    return nuevoRegistro.key;
  } catch (error) {
    console.error(`❌ Error al crear registro:`, error.message);
    throw error;
  }
};

// Genera el id que tendría un registro nuevo, sin guardarlo (para crear varios en una sola operación).
export const generarId = (ruta) => push(ref(db, ruta)).key;

export const actualizarRegistro = async (ruta, nuevosDatos) => {
  if (!ruta) throw new Error("Se requiere una ruta válida");

  try {
    console.log("✏️ Actualizando:", ruta);
    await set(ref(db, ruta), nuevosDatos);
    console.log("✅ Registro actualizado correctamente");

    crearLog(ruta, "actualizar");

    return true;
  } catch (error) {
    console.error(`❌ Error al actualizar:`, error.message);
    throw error;
  }
};

export const actualizarMultiple = async (actualizaciones) => {
  if (!actualizaciones || !Object.keys(actualizaciones).length) return true;

  try {
    console.log(`✏️ Actualizando ${Object.keys(actualizaciones).length} rutas en una sola operación`);
    await update(ref(db), actualizaciones);
    console.log("✅ Actualización múltiple completada");

    crearLog(`multi (${Object.keys(actualizaciones).length} rutas)`, "actualizar");

    return true;
  } catch (error) {
    console.error("❌ Error al actualizar múltiple:", error.message);
    throw error;
  }
};

export const eliminarRegistro = async (ruta) => {
  if (!ruta) throw new Error("Se requiere una ruta válida");

  try {
    console.log("🗑️ Eliminando:", ruta);
    await remove(ref(db, ruta));
    console.log("✅ Registro eliminado correctamente");

    crearLog(ruta, "eliminar");

    return true;
  } catch (error) {
    console.error(`❌ Error al eliminar:`, error.message);
    throw error;
  }
};
